import React, { useMemo } from 'react';
import { CollapseBlock } from '../../../../../common/index.jsx';
import FormTextField from '../../../../../antd/form/FormTextField/FormTextField.jsx';
import { useTranslation } from 'react-i18next';
import {
  number,
  required,
  url,
} from '../../../../../antd/form/FormValidation.js';
import Form from '../../../../../antd/form/Form/Form.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import FormNumberField from '../../../../../antd/form/FormNumberField/FormNumberField.jsx';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import FormCheckBox from '../../../../../antd/form/FormCheckBox/FormCheckBox.jsx';
import FormTextEditor from '../../../../../antd/form/FormTextEditor/FormTextEditor.jsx';
import { makeLecturerableOption } from '../../../../../../utils/helpers/CommonHelper.jsx';

export const EduCourseForm = ({ initials, eduLecturers, onSubmit }) => {
  const { t } = useTranslation();

  // Normalize initial values so the checkbox gets a boolean even if API sent "Áno"/"Nie"
  const normalizedInitials = useMemo(() => {
    if (!initials) return initials;
    return {
      ...initials,
      ends_with_certificate: initials.ends_with_certificate === 'Áno',
    };
  }, [initials]);

  // Map checkbox boolean to localized string for submission
  const handleSubmit = (values) => {
    const mapped = {
      ...values,
      ends_with_certificate: values?.ends_with_certificate ? 'Áno' : 'Nie',
    };
    onSubmit(mapped);
  };

  const difficultyOptions = useMemo(
    () => [
      {
        label: t('začiatočník'),
        value: 'začiatočník',
      },
      {
        label: t('mierne pokročilý'),
        value: 'mierne pokročilý',
      },
      {
        label: t('pokročilý'),
        value: 'pokročilý',
      },
      {
        label: t('expert'),
        value: 'expert',
      },
    ],
    [t],
  );

  const lecturersOptions = useMemo(() => {
    const lecturers = [];

    (eduLecturers?.externs || []).forEach((extern) => {
      lecturers.push(makeLecturerableOption(extern));
    });
    (eduLecturers?.persons || []).forEach((person) => {
      lecturers.push(makeLecturerableOption(person));
    });

    return lecturers;
  }, [eduLecturers]);

  return (
    <Form onFinish={handleSubmit} initialValues={normalizedInitials}>
      <CollapseBlock title={t('Základné informácie')} collapsible>
        <FormTextField
          label={t('Názov kurzu')}
          name={'name'}
          rules={[required(t)]}
        />
        <FormSelect
          label={t('Náročnosť')}
          name={'difficulty'}
          options={difficultyOptions}
          rules={[required(t)]}
        />
        <FormTextField
          label={t('Celkom hodín')}
          name={'number_hours'}
          rules={[required(t)]}
          style={{ width: '100%' }}
          extra={t('Počet hodín sa uvádza v 45 min. blokoch')}
        />
        <FormNumberField
          label={t('Cena na osobu')}
          name={'price'}
          rules={[required(t), number(t)]}
          style={{ width: '100%' }}
        />
        <FormTextEditor
          label={t('Program')}
          name={'agenda'}
          rules={[required(t)]}
        />
        <FormCheckBox
          label={t('Kurz končí certifikátom')}
          name={'ends_with_certificate'}
        />
      </CollapseBlock>

      <div style={{ marginBottom: '10px' }}>
        <CollapseBlock title={t('Lektori')} collapsible>
          <FormSelect
            name="lecturerable"
            label={t('Hlavný lektor')}
            options={lecturersOptions}
            rules={[required(t)]}
          />
          <FormSelect
            name="other_lecturers"
            label={t('Ďalší lektori')}
            options={lecturersOptions}
            mode="multiple"
          />
        </CollapseBlock>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <CollapseBlock title={t('Rozšírené informácie')} collapsible>
          <FormTextField
            label={t('Akreditácia kurzu')}
            name={'accreditation'}
            rules={[{ type: 'string' }]}
          />
          <FormTextField
            style={{ marginTop: '10px' }}
            label={t('Odporúčaný kurz pred (link)')}
            name={'before_course_link'}
            rules={[url(t)]}
          />
          <FormTextField
            label={t('Odporúčaný kurz po (link)')}
            name={'after_course_link'}
            rules={[url(t)]}
          />
          <FormTextField label={t('Cieľová skupina')} name={'target_group'} />
          <FormTextEditor label={t('Poznámky')} name={'notes'} />
        </CollapseBlock>
      </div>
      <FormButtonsContainer>
        <FormButton submitOnClick={true}>{t('Uložiť')}</FormButton>
      </FormButtonsContainer>
    </Form>
  );
};
